/*
  Licensed under the Apache License, Version 2.0 (the "License"); you may not use
  this file except in compliance with the License. You may obtain a copy of the
  License at

      https://www.apache.org/licenses/LICENSE-2.0

  Unless required by applicable law or agreed to in writing, software distributed
  under the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR
  CONDITIONS OF ANY KIND, either express or implied. See the License for the
  specific language governing permissions and limitations under the License.
*/

import makeStyles from "@mui/styles/makeStyles";
import classnames from "classnames";
import React, { useEffect, useRef } from "react";

// The MUI Info icon, as a mask over the attribution button. Inlined rather
// than rendered from @mui/icons-material so that neither react-dom/server nor
// an svg-to-data-uri encoder is pulled into the bundle for one glyph.
const INFO_ICON_MASK =
  "url(\"data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'%3e%3cpath fill='currentColor' d='M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2m1 15h-2v-6h2zm0-8h-2V7h2z'/%3e%3c/svg%3e\")";

const useStyles = makeStyles((theme) => ({
  root: {
    "& .mapboxgl-ctrl-attrib": {
      "&.mapboxgl-compact": {
        margin: 0,
        backgroundColor: "white",
        borderRadius: 4, // match the box
        outlineColor: theme.palette.grey["700"], // use outline color as secondary border
        outlineOffset: -7,
        outlineWidth: 7,
        outlineStyle: "solid",
        maxWidth: 300,
        minWidth: 30,
        minHeight: 28,
        boxShadow:
          "0px 5px 5px -3px rgba(0,0,0,0.2),0px 8px 10px 1px rgba(0,0,0,0.14),0px 3px 14px 2px rgba(0,0,0,0.12)",
        fontSize: "clamp(10px, 1vw, 12px)",
        "&:hover": {
          cursor: "pointer",
          outlineColor: theme.palette.grey["600"],
          "& .mapboxgl-ctrl-attrib-button": {
            boxShadow: "none",
            backgroundColor: theme.palette.grey["600"],
          },
        },
        "&:active, &:hover:active": {
          outlineColor: theme.palette.grey["500"],
          "& .mapboxgl-ctrl-attrib-button": {
            backgroundColor: theme.palette.grey["500"],
          },
        },
        "&.mapboxgl-compact-show": {
          backgroundColor: "hsla(0,0%,100%,.5)",
          outline: "none",
          paddingRight: 32,
          "&:hover": {
            cursor: "default",
          },
          "& .mapboxgl-ctrl-attrib-button": {
            width: 30,
            height: 28,
          },
          // Keep the expanded attribution text on screen next to the controls
          [theme.breakpoints.down("sm")]: {
            maxWidth: "calc(100vw - 232px)",
          },
        },
        "& .mapboxgl-ctrl-attrib-button": {
          top: "auto",
          backgroundImage: "none", // remove original icon
          backgroundColor: theme.palette.grey["700"],
          maskImage: INFO_ICON_MASK,
          bottom: 0,
          width: "100%",
          height: "100%",
        },
      },
    },
    "& .mapboxgl-ctrl-attrib-inner": {
      textAlign: "right",
      color: theme.palette.grey["800"],
      "&:hover": {
        color: theme.palette.grey["900"],
      },
      // show only our customized map feedback link, not the generic one
      '& a[title="Improve this map"]': {
        display: "none",
      },
    },
  },
}));

/**
 * Nearest attribution control to the slot: the first ancestor holding one, so
 * a second map on the page keeps its own.
 */
const findAttribution = (slot: HTMLElement): Element | null => {
  for (let node = slot.parentElement; node; node = node.parentElement) {
    const found = node.querySelector(".mapboxgl-ctrl-attrib");
    if (found) {
      return found;
    }
  }
  return null;
};

interface IAttributionSlotProps {
  className?: string;
}

/**
 * Hosts mapbox's own attribution control in the map-controls row, styled as a
 * square info button, rather than leaving it in mapbox's corner. The corner
 * copy has to be hidden by the map wrapper's own styles, since it sits outside
 * this subtree until it is moved.
 */
export const AttributionSlot = (props: IAttributionSlotProps) => {
  const { className } = props;
  const classes = useStyles();
  const slotRef = useRef<HTMLDivElement>();
  const attributionNodeRef = useRef<Element | null>(null);
  const cornerRef = useRef<Element | null>(null);

  // Runs every render and is idempotent, so it also recovers the control if
  // mapbox ever re-adds it.
  useEffect(() => {
    const slot = slotRef.current;
    const attribution = slot && findAttribution(slot);
    if (attribution && attribution.parentElement !== slot) {
      cornerRef.current = attribution.parentElement;
      slot.appendChild(attribution);
    }
    attributionNodeRef.current = attribution || null;
  });

  // Hand the control back to mapbox's corner when the slot goes away: React
  // takes the slot out of the DOM with the control still inside it, and a
  // detached node is no longer reachable from a slot mounted in its place.
  useEffect(() => {
    // Captured here rather than read back in the cleanup: React detaches the
    // ref before the cleanup runs.
    const slot = slotRef.current;

    return () => {
      const attribution = attributionNodeRef.current;
      const corner = cornerRef.current;
      if (attribution && corner && attribution.parentElement === slot) {
        corner.appendChild(attribution);
      }
    };
  }, []);

  return (
    <div
      ref={slotRef}
      className={classnames(
        "marapp-qa-attribution-slot",
        classes.root,
        className
      )}
    />
  );
};

export default AttributionSlot;
